/**
 * AirdropForm Component
 *
 * Main form for executing token airdrops to selected recipients.
 * Allows selection of recipients (via table or bulk paste), currency,
 * and amount per user.
 *
 * @component
 */

import React, { useState, useCallback } from "react";

// Shared imports
import { MAX_RECIPIENTS } from "./constants";
import { colors, gradients } from "./styles";
import {
  WalletSelector,
  CurrencySelector,
  AirdropResult,
  DistributionConfig,
  DistributionSummary,
  AccentButton,
  PrimaryButton,
} from "./components";

// ============================================================================
// Main Component
// ============================================================================

const AirdropForm = ({
  currencies,
  currenciesLoading,
  hederaWallets,
  walletsLoading,
  payoutWalletAddress,
  onAirdrop,
  onFetchBalance,
  loading,
}) => {
  // Wallet selector state - Map of address -> wallet object for full recipient data
  const [selectedWallets, setSelectedWallets] = useState(new Map());
  const [searchQuery, setSearchQuery] = useState("");

  // Bulk paste state - persisted across tab switches
  const [bulkInputText, setBulkInputText] = useState("");
  const [bulkParseResult, setBulkParseResult] = useState(null);

  // Track selection source to enforce mutual exclusivity
  const [selectionSource, setSelectionSource] = useState(null); // 'table' or 'paste'

  // Distribution state
  const [distributeAmount, setDistributeAmount] = useState("");
  const [distributeCurrency, setDistributeCurrency] = useState("");
  const [airdropResult, setAirdropResult] = useState(null);

  // Balance state for distribution validation
  const [distributionBalance, setDistributionBalance] = useState(null);
  const [distributionBalanceLoading, setDistributionBalanceLoading] =
    useState(false);

  // Fetch balance when distribution currency changes
  const handleDistributeCurrencyChange = async (selectedSymbol) => {
    setDistributeCurrency(selectedSymbol);
    setDistributionBalance(null);

    if (selectedSymbol && onFetchBalance) {
      const selectedCurrency = currencies.find(
        (c) => (c.symbol || c) === selectedSymbol,
      );
      const tokenId = selectedCurrency?.tokenId;

      if (tokenId) {
        setDistributionBalanceLoading(true);
        try {
          const balanceData = await onFetchBalance(tokenId);
          if (balanceData) {
            setDistributionBalance(parseFloat(balanceData.balance) || 0);
          }
        } catch (error) {
          console.error("Error fetching balance:", error);
        } finally {
          setDistributionBalanceLoading(false);
        }
      }
    }
  };

  // Calculate total distribution amount
  const totalDistribution =
    parseFloat(distributeAmount || 0) * selectedWallets.size;
  const hasInsufficientBalance =
    distributionBalance !== null && totalDistribution > distributionBalance;

  // Wallet selection handlers
  const handleToggleWallet = useCallback((wallet) => {
    setSelectedWallets((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(wallet.address)) {
        newMap.delete(wallet.address);
        if (newMap.size === 0) {
          setSelectionSource(null);
        }
      } else if (newMap.size < MAX_RECIPIENTS) {
        newMap.set(wallet.address, wallet);
        setSelectionSource("table");
      }
      return newMap;
    });
  }, []);

  const handleSelectAll = useCallback(
    (wallets) => {
      const newMap = new Map(selectedWallets);
      wallets.forEach((w) => {
        if (newMap.size < MAX_RECIPIENTS) {
          newMap.set(w.address, w);
        }
      });
      setSelectedWallets(newMap);
      if (newMap.size > 0) {
        setSelectionSource("table");
      }
    },
    [selectedWallets],
  );

  const handleDeselectAll = useCallback(() => {
    setSelectedWallets(new Map());
    setSelectionSource(null);
  }, []);

  // Handle bulk add from paste functionality
  const handleBulkAdd = useCallback((wallets) => {
    setSelectedWallets((prev) => {
      const newMap = new Map(prev);
      wallets.forEach((wallet) => {
        if (newMap.size < MAX_RECIPIENTS && !newMap.has(wallet.address)) {
          newMap.set(wallet.address, wallet);
        }
      });
      return newMap;
    });
    setSelectionSource("paste");
  }, []);

  // Airdrop submission handler
  const handleAirdrop = async (e) => {
    e.preventDefault();

    const recipientWallets = Array.from(selectedWallets.values());

    if (recipientWallets.length === 0) {
      alert("Please select or enter at least one recipient");
      return;
    }

    if (recipientWallets.length > MAX_RECIPIENTS) {
      alert(`Maximum ${MAX_RECIPIENTS} recipients allowed`);
      return;
    }

    if (!distributeAmount || !distributeCurrency) {
      alert("Please enter amount and select currency");
      return;
    }

    const selectedCurrency = currencies.find(
      (c) => (c.symbol || c) === distributeCurrency,
    );
    const tokenId = selectedCurrency?.tokenId;

    if (!tokenId) {
      alert(`Cannot find token ID for currency: ${distributeCurrency}`);
      return;
    }

    if (hasInsufficientBalance) {
      alert(
        `Insufficient balance. You need ${totalDistribution.toFixed(
          2,
        )} ${distributeCurrency} but only have ${distributionBalance.toFixed(
          2,
        )} ${distributeCurrency} available.`,
      );
      return;
    }

    // Format recipients for the API
    const allFormattedRecipients = recipientWallets.map((wallet) => ({
      walletAddress: wallet.address,
      userId: wallet.user_id ? wallet.user_id.toString() : "",
      username: wallet.name || "",
      email: wallet.email || "",
    }));

    // Filter out incomplete ones
    const formattedRecipients = allFormattedRecipients.filter((recipient) => {
      return recipient.userId && recipient.username && recipient.email;
    });

    // Check if any recipients were excluded
    const excludedCount = recipientWallets.length - formattedRecipients.length;
    if (excludedCount > 0) {
      const proceed = confirm(
        `Warning: ${excludedCount} wallet(s) will be excluded because they don't have complete user information (user ID, username, or email).\n\n` +
          `Proceeding with ${formattedRecipients.length} recipient(s).\n\n` +
          `Do you want to continue?`,
      );
      if (!proceed) {
        return;
      }
    }

    if (formattedRecipients.length === 0) {
      alert(
        "Cannot proceed: None of the selected wallets have complete user information.\n" +
          "API requires: User ID, Username, and Email for each recipient.\n\n" +
          "Please select wallets that are associated with user accounts.",
      );
      return;
    }

    console.log("Sending airdrop with recipients:", formattedRecipients);
    console.log("Token ID:", tokenId);

    const result = await onAirdrop(
      formattedRecipients,
      distributeAmount,
      distributeCurrency,
      tokenId,
    );

    if (result) {
      setAirdropResult(result);
      setBulkInputText("");
      setBulkParseResult(null);
      setSelectionSource(null);
      // Refresh balance after airdrop
      if (onFetchBalance) {
        const balanceData = await onFetchBalance(tokenId);
        if (balanceData) {
          setDistributionBalance(parseFloat(balanceData.balance) || 0);
        }
      }
    }
  };

  // Reset form handler
  const resetForm = () => {
    setSelectedWallets(new Map());
    setDistributeAmount("");
    setDistributeCurrency("");
    setDistributionBalance(null);
    setAirdropResult(null);
    setSearchQuery("");
    setBulkInputText("");
    setBulkParseResult(null);
    setSelectionSource(null);
  };

  const recipientCount = selectedWallets.size;

  return (
    <div className="airdrop-form">
      <div className="form-section distribute-section">
        <form
          onSubmit={handleAirdrop}
          style={{
            background: "#fafafa",
            padding: "24px",
            borderRadius: "6px",
            border: `1px solid ${colors.border}`,
          }}
        >
          {/* Recipient Selection */}
          <WalletSelector
            wallets={hederaWallets}
            selectedWallets={selectedWallets}
            onToggleWallet={handleToggleWallet}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onBulkAdd={handleBulkAdd}
            loading={walletsLoading}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            bulkInputText={bulkInputText}
            setBulkInputText={setBulkInputText}
            bulkParseResult={bulkParseResult}
            setBulkParseResult={setBulkParseResult}
            selectionSource={selectionSource}
            setSelectionSource={setSelectionSource}
          />

          {/* Currency Selection */}
          <div style={{ marginBottom: "20px" }}>
            <CurrencySelector
              currencies={currencies}
              value={distributeCurrency}
              onChange={handleDistributeCurrencyChange}
              disabled={loading || currenciesLoading}
              loading={currenciesLoading}
            />
          </div>

          {/* Amount and Balance */}
          <DistributionConfig
            distributeAmount={distributeAmount}
            setDistributeAmount={setDistributeAmount}
            distributeCurrency={distributeCurrency}
            distributionBalance={distributionBalance}
            distributionBalanceLoading={distributionBalanceLoading}
            hasInsufficientBalance={hasInsufficientBalance}
            loading={loading}
          />

          {/* Summary */}
          <DistributionSummary
            recipientCount={recipientCount}
            distributeAmount={distributeAmount}
            distributeCurrency={distributeCurrency}
            totalDistribution={totalDistribution}
            hasInsufficientBalance={hasInsufficientBalance}
          />

          {/* Insufficient Balance Warning */}
          {hasInsufficientBalance && (
            <div className="flash flash_error">
              <span>
                ⚠️ Insufficient balance. You need {totalDistribution.toFixed(2)}{" "}
                {distributeCurrency} but only have{" "}
                {distributionBalance.toFixed(2)} {distributeCurrency} available.
              </span>
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginTop: "24px",
              paddingTop: "20px",
              borderTop: `1px solid ${colors.border}`,
              flexWrap: "wrap",
            }}
          >
            <AccentButton
              type="submit"
              disabled={
                loading ||
                recipientCount === 0 ||
                !distributeAmount ||
                !distributeCurrency ||
                hasInsufficientBalance
              }
              style={{
                padding: "14px 28px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {loading ? (
                <>
                  <span className="spinner-sm"></span>
                  Distributing...
                </>
              ) : (
                `Submit Airdrop to ${recipientCount} Users`
              )}
            </AccentButton>
            <PrimaryButton
              type="button"
              onClick={resetForm}
              style={{
                padding: "14px 24px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Reset Form
            </PrimaryButton>
          </div>
        </form>

        {/* Airdrop Results */}
        <AirdropResult result={airdropResult} />
      </div>
    </div>
  );
};

export default AirdropForm;
